import React, { useState, useEffect } from 'react';
import AuthorFields from '../components/AuthorFields';
import CitationResult from '../components/CitationResult';
import { formatAuthorsForCitation, formatAuthorsForParenthetical, formatAuthorsForReference, formatURL } from '../utils/citationHelpers';

function VisualMaterialForm() {
  const [authors, setAuthors] = useState([]);
  const [authorType, setAuthorType] = useState('personal');
  const [hasAuthors, setHasAuthors] = useState(true);
  const [formData, setFormData] = useState({
    artistName: '',
    title: '',
    materialType: 'Fotografía',
    customMaterialType: '',
    year: '',
    portal: '',
    url: ''
  });
  const [result, setResult] = useState(null);
  const [liveResult, setLiveResult] = useState(null);

  const materialTypes = [
    'Canva', 'Diapositivas de PowerPoint', 'Fotografía', 'Ícono',
    'Imagen', 'Infografía', 'Logo', 'Mapa', 'Personalizar', 'Vector'
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleHasAuthorsChange = (e) => {
    setHasAuthors(e.target.checked);
    if (!e.target.checked) {
      setAuthors([]);
    }
  };

  // Genera una vista previa en tiempo real
  useEffect(() => {
    if (formData.title && (formData.year || formData.year === 's.f.' || formData.year === 's. f.')) {
      if (hasAuthors && authors.length > 0) {
        generatePreview();
      } else if (!hasAuthors && formData.artistName) {
        generatePreviewNoAuthor();
      }
    } else {
      setLiveResult(null);
    }
  }, [authors, formData, hasAuthors]);

  const getMaterialTypeLabel = () => {
    return formData.materialType === 'Personalizar' ? formData.customMaterialType : formData.materialType;
  };

  const generatePreviewNoAuthor = () => {
    const displayYear = formData.year === 's.f.' || formData.year === 's. f.' ? formData.year : formData.year;

    const citation = {
      narrative: `${formData.artistName} (${displayYear})`,
      parenthetical: `(${formData.artistName}, ${displayYear})`
    };

    const materialTypeLabel = getMaterialTypeLabel();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${formData.artistName}. (${displayYear}). <em>${formData.title}</em> [${materialTypeLabel}]`;
    
    if (formData.portal) {
      referenceText += `. ${formData.portal}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generatePreview = () => {
    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;
    const displayYear = formData.year === 's.f.' || formData.year === 's. f.' ? formData.year : formData.year;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${displayYear})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${displayYear})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${displayYear})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${displayYear})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${displayYear})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${displayYear})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${displayYear})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${displayYear})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const materialTypeLabel = getMaterialTypeLabel();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${displayYear}). <em>${formData.title}</em> [${materialTypeLabel}]`;
    
    if (formData.portal) {
      referenceText += `. ${formData.portal}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setLiveResult({ citation, reference });
  };

  const generateCitation = () => {
    if (!formData.title || (!formData.year && formData.year !== 's.f.' && formData.year !== 's. f.')) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, completa todos los campos obligatorios.');
      return;
    }

    if (hasAuthors && authors.length === 0) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, agrega al menos un autor o especifica el nombre del artista/usuario.');
      return;
    }

    if (!hasAuthors && !formData.artistName) {
      alert('Generador APA, séptima edición dice:\n\nPor favor, especifica el nombre del artista, usuario o seudónimo.');
      return;
    }

    if (!hasAuthors) {
      const displayYear = formData.year === 's.f.' || formData.year === 's. f.' ? formData.year : formData.year;

      const citation = {
        narrative: `${formData.artistName} (${displayYear})`,
        parenthetical: `(${formData.artistName}, ${displayYear})`
      };

      const materialTypeLabel = getMaterialTypeLabel();
      const formattedUrl = formatURL(formData.url);

      let referenceText = `${formData.artistName}. (${displayYear}). <em>${formData.title}</em> [${materialTypeLabel}]`;
      
      if (formData.portal) {
        referenceText += `. ${formData.portal}`;
      }
      
      if (formattedUrl) {
        referenceText += `. ${formattedUrl}`;
      } else {
        referenceText += '.';
      }

      const reference = {
        html: referenceText,
        text: referenceText.replace(/<[^>]*>/g, '')
      };

      setResult({ citation, reference });
      return;
    }

    // Verificar si hay autores institucionales con siglas
    const institutionalAuthors = authors.filter(
      author => author.type === 'institutional' && author.abbreviation
    );

    let narrativeCitation, parentheticalCitation, narrativeSubsequent, parentheticalSubsequent;
    const displayYear = formData.year === 's.f.' || formData.year === 's. f.' ? formData.year : formData.year;

    if (institutionalAuthors.length === 1 && (authorType === 'institutional' || authorType === 'both')) {
      // Para cita narrativa con institucional con sigla: "Nombre (SIGLA, año)"
      narrativeCitation = `${institutionalAuthors[0].name} (${institutionalAuthors[0].abbreviation}, ${displayYear})`;
      
      // Para cita parentética: "(Nombre [SIGLA], año)"
      parentheticalCitation = `(${institutionalAuthors[0].name} [${institutionalAuthors[0].abbreviation}], ${displayYear})`;
      
      // Para cita narrativa subsecuente: "SIGLA (año)"
      narrativeSubsequent = `${institutionalAuthors[0].abbreviation} (${displayYear})`;
      
      // Para cita parentética subsecuente: "(SIGLA, año)"
      parentheticalSubsequent = `(${institutionalAuthors[0].abbreviation}, ${displayYear})`;
    } else {
      // Caso normal sin autor institucional con sigla o múltiples autores
      const authorsForCitation = formatAuthorsForCitation(authors, authorType);
      const authorsForParenthetical = formatAuthorsForParenthetical(authors, authorType);
      const authorsForCitationSubsequent = formatAuthorsForCitation(authors, authorType, false);
      const authorsForParentheticalSubsequent = formatAuthorsForParenthetical(authors, authorType, false);

      narrativeCitation = `${authorsForCitation} (${displayYear})`;
      parentheticalCitation = `(${authorsForParenthetical}, ${displayYear})`;
      narrativeSubsequent = `${authorsForCitationSubsequent} (${displayYear})`;
      parentheticalSubsequent = `(${authorsForParentheticalSubsequent}, ${displayYear})`;
    }

    const citation = {
      narrative: narrativeCitation,
      parenthetical: parentheticalCitation,
      narrativeSubsequent: narrativeSubsequent,
      parentheticalSubsequent: parentheticalSubsequent
    };

    const authorsForReference = formatAuthorsForReference(authors, authorType);
    const materialTypeLabel = getMaterialTypeLabel();
    const formattedUrl = formatURL(formData.url);

    let referenceText = `${authorsForReference} (${displayYear}). <em>${formData.title}</em> [${materialTypeLabel}]`;
    
    if (formData.portal) {
      referenceText += `. ${formData.portal}`;
    }
    
    if (formattedUrl) {
      referenceText += `. ${formattedUrl}`;
    } else {
      referenceText += '.';
    }

    const reference = {
      html: referenceText,
      text: referenceText.replace(/<[^>]*>/g, '')
    };

    setResult({ citation, reference });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2 dark:text-gray-100">Material visual</h1>
        <p className="text-gray-600 dark:text-gray-400">Fotografías, imágenes prediseñadas, banco de imágenes y otros trabajos visuales</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6 dark:bg-gray-800 dark:border-gray-700">
          <div>
            <label className="flex items-center mb-4">
              <input
                type="checkbox"
                checked={hasAuthors}
                onChange={handleHasAuthorsChange}
                className="mr-2 dark:bg-gray-700 dark:border-gray-600"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                El material visual tiene autor(es) personal(es) o institucional(es)
              </span>
            </label>
            {!hasAuthors && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4 dark:bg-yellow-900/20 dark:border-yellow-800/50">
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  <strong>Sin autor formal:</strong> Especifica el nombre del artista, usuario o seudónimo.
                </p>
              </div>
            )}
          </div>

          {hasAuthors && (
            <AuthorFields
              authors={authors}
              setAuthors={setAuthors}
              authorType={authorType}
              setAuthorType={setAuthorType}
            />
          )}

          {!hasAuthors && (
            <div>
              <label htmlFor="artistName" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Nombre del artista, usuario o seudónimo *
              </label>
              <input
                type="text"
                id="artistName"
                name="artistName"
                value={formData.artistName}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                required
              />
            </div>
          )}

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Título del trabajo visual *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              required
            />
          </div>

          <div>
            <label htmlFor="materialType" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Tipo de material visual
            </label>
            <select
              id="materialType"
              name="materialType"
              value={formData.materialType}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
            >
              {materialTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {formData.materialType === 'Personalizar' && (
            <div>
              <label htmlFor="customMaterialType" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Tipo personalizado
              </label>
              <input
                type="text"
                id="customMaterialType"
                name="customMaterialType"
                value={formData.customMaterialType}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
                placeholder="ej: Ilustración, Gráfico, etc."
              />
            </div>
          )}

          <div>
            <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Año *
            </label>
            <input
              type="text"
              id="year"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="2024 o s.f. o s. f."
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Usar "s.f." o "s. f." si no hay fecha
            </p>
          </div>

          <div>
            <label htmlFor="portal" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              Nombre del portal del trabajo visual
            </label>
            <input
              type="text"
              id="portal"
              name="portal"
              value={formData.portal}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="ej: Scopio, IconArchive, Unsplash"
            />
          </div>

          <div>
            <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              URL *
            </label>
            <input
              type="text"
              id="url"
              name="url"
              value={formData.url}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              placeholder="URL (con o sin https://)"
              required
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Puede introducir una URL con o sin el prefijo https://
            </p>
          </div>

          <button
            type="button"
            onClick={generateCitation}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Generar cita y referencia
          </button>
        </div>

        <div className="space-y-6">
          {!result && liveResult && (
            <CitationResult
              citation={liveResult.citation}
              reference={liveResult.reference}
              livePreview={true}
            />
          )}
          <CitationResult
            citation={result?.citation}
            reference={result?.reference}
          />
        </div>
      </div>
    </div>
  );
}

export default VisualMaterialForm;